import React, { useState } from 'react';
import {
    Zap,
    CalendarCheck,
    Mail,
    Database,
    ShieldAlert,
    Users,
    ClipboardCheck,
    Share,
    TerminalSquare,
    PackageSearch,
    MessageSquare,
    Briefcase,
    HandCoins,
    CalendarClock,
    BarChart3,
    CheckCircle,
    AlertTriangle,
    Clock,
    PlayCircle
} from 'lucide-react';
import WorkflowInputModal from './WorkflowInputModal';

const mockWorkflows = [
    {
        id: 'sync_crm',
        name: 'Sync CRM Data',
        description: 'Synchronizes recent updates from internal databases with the CRM system.',
        icon: <Database className="w-5 h-5 text-blue-400" />,
        requiresInput: false,
        allowedRoles: ['admin', 'manager'],
        status: 'Failed',
        lastRun: '2025-05-16 01:59'
    },
    {
        id: 'daily_summary',
        name: 'Send Daily Summary',
        description: 'Dispatches project summaries to all team leads. May require date and department.',
        icon: <Mail className="w-5 h-5 text-teal-400" />,
        requiresInput: true,
        allowedRoles: ['manager'],
        status: 'Pending',
        lastRun: '2025-05-20 23:33'
    },
    {
        id: 'audit_trigger',
        name: 'Trigger Audit Review',
        description: 'Runs compliance checks across all projects. Usually triggered weekly.',
        icon: <ShieldAlert className="w-5 h-5 text-red-400" />,
        requiresInput: false,
        allowedRoles: ['admin'],
        status: 'Pending',
        lastRun: '2025-05-24 22:20'
    },
    {
        id: 'resource_check',
        name: 'Resource Utilization Check',
        description: 'Analyzes resource load and returns optimization suggestions. May request timeframe.',
        icon: <Users className="w-5 h-5 text-yellow-400" />,
        requiresInput: true,
        allowedRoles: ['admin', 'manager'],
        status: 'Pending',
        lastRun: '2025-05-24 11:34'
    },
    {
        id: 'task_closeout',
        name: 'Close Out Tasks',
        description: 'Bulk-closes tasks that meet “done” conditions. Confirmation may be required.',
        icon: <ClipboardCheck className="w-5 h-5 text-green-400" />,
        requiresInput: false,
        allowedRoles: ['manager'],
        status: 'Failed',
        lastRun: '2025-05-20 21:53'
    },
    {
        id: 'review_project_kpis',
        name: 'Review Project KPIs',
        description: 'Compiles key performance indicators across all active projects. May ask for date range.',
        icon: <BarChart3 className="w-5 h-5 text-blue-400" />,
        requiresInput: true,
        allowedRoles: ['manager', 'director'],
        status: 'Success',
        lastRun: '2025-05-16 07:05'
    },
    {
        id: 'team_shift_schedule',
        name: 'Generate Shift Schedule',
        description: 'Creates the next week’s shift plan based on capacity and leave calendar.',
        icon: <CalendarClock className="w-5 h-5 text-purple-400" />,
        requiresInput: true,
        allowedRoles: ['manager'],
        status: 'Pending',
        lastRun: '2025-05-24 08:49'
    },
    {
        id: 'budget_reallocation',
        name: 'Initiate Budget Reallocation',
        description: 'Reassigns budget from under-utilized departments. Reason input required.',
        icon: <HandCoins className="w-5 h-5 text-amber-400" />,
        requiresInput: true,
        allowedRoles: ['director'],
        status: 'Failed',
        lastRun: '2025-05-21 11:08'
    },
    {
        id: 'handover_responsibility',
        name: 'Handover Project Responsibility',
        description: 'Transfers ownership to another manager. Requires selecting assignee.',
        icon: <Briefcase className="w-5 h-5 text-teal-400" />,
        requiresInput: true,
        allowedRoles: ['manager', 'director'],
        status: 'Failed',
        lastRun: '2025-05-23 20:49'
    },
    {
        id: 'client_alert_broadcast',
        name: 'Broadcast Client Alert',
        description: 'Sends system maintenance or delay notices to selected clients.',
        icon: <MessageSquare className="w-5 h-5 text-red-400" />,
        requiresInput: true,
        allowedRoles: ['manager'],
        status: 'Success',
        lastRun: '2025-05-20 04:20'
    },
    {
        id: 'compliance_healthcheck',
        name: 'Run Compliance Health Check',
        description: 'Validates if internal processes meet audit requirements.',
        icon: <TerminalSquare className="w-5 h-5 text-pink-400" />,
        requiresInput: false,
        allowedRoles: ['director'],
        status: 'Pending',
        lastRun: '2025-05-20 01:21'
    },
    {
        id: 'delegate_standup_ownership',
        name: 'Delegate Standup Ownership',
        description: 'Assigns daily standup hosting to a new team lead.',
        icon: <Share className="w-5 h-5 text-indigo-400" />,
        requiresInput: true,
        allowedRoles: ['manager'],
        status: 'Pending',
        lastRun: '2025-05-18 01:24'
    },
    {
        id: 'inventory_health_report',
        name: 'Generate Inventory Health Report',
        description: 'Summarizes inventory status with warnings on low stock.',
        icon: <PackageSearch className="w-5 h-5 text-lime-400" />,
        requiresInput: false,
        allowedRoles: ['manager', 'director'],
        status: 'Failed',
        lastRun: '2025-05-16 01:20'
    }
];

const getStatusIcon = (status: string) => {
    switch (status) {
        case 'Success':
            return <CheckCircle className="w-4 h-4 text-green-400" />;
        case 'Failed':
            return <AlertTriangle className="w-4 h-4 text-red-400" />;
        case 'Pending':
        default:
            return <Clock className="w-4 h-4 text-yellow-400" />;
    }
};

// Simulate role — in production this comes from auth
const currentUserRole = 'manager';


const StandaloneWorkflows = () => {
    const available = mockWorkflows.filter(wf =>
        wf.allowedRoles.includes(currentUserRole)
    );
 const [showModal, setShowModal] = useState(false);
const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(null);

const handleTrigger = (id: string) => {
    setActiveWorkflowId(id);
    setShowModal(true);
};

const handleSubmit = (formData: Record<string, any>) => {
    console.log('Submitted values for', activeWorkflowId, formData);
    // You can trigger your real workflow API here
};


    return (
        <div className="p-6 text-white">
            <h2 className="text-2xl font-semibold text-indigo-400 mb-6 flex items-center gap-2">
                <Zap className="w-6 h-6 text-indigo-400" />  Workflows
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {available.map(wf => (
                    <div
                        key={wf.id}
                        className="relative group bg-gradient-to-br from-neutral-900 via-neutral-900 to-black border border-neutral-800 rounded-xl p-5 shadow-md hover:shadow-xl transition-all"
                    >
                        {/* Floating Icon */}
                        <div className="absolute top-3 right-3 bg-neutral-800 p-2 rounded-full shadow-inner">
                            {wf.icon}
                        </div>

                        {/* Title + ID */}
                        <div className="mb-2">
                            <h3 className="text-base font-semibold text-white">{wf.name}</h3>
                            <p className="text-[11px] text-gray-500 font-mono">#{wf.id}</p>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-300 mb-2">{wf.description}</p>

                        {/* Optional Input Info */}
                        {wf.requiresInput && (
                            <p className="text-[11px] text-yellow-300 italic mb-2">
                                ⚠️ Requires additional input like date, team, or note.
                            </p>
                        )}

                        {/* Status + Last Run */}
                        <div className="text-[11px] text-gray-400 flex items-center gap-2 mb-4">
                            {getStatusIcon(wf.status)}
                            <span>Last Run: {wf.lastRun}</span>
                        </div>

                        {/* Trigger Button */}
                        <div className="pt-2 border-t border-neutral-800 mt-3 flex justify-end">
                            <button
                                onClick={() => handleTrigger(wf.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-all"
                            >
                                <PlayCircle className="w-4 h-4" /> Trigger
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {showModal && (
                <WorkflowInputModal
                    onClose={() => setShowModal(false)}
                    onSubmit={handleSubmit}
                />
            )}
        </div>
    );
};

export default StandaloneWorkflows;
